import React from 'react';
import { useErrorHandler } from '~/hooks/useErrorHandler';
import { useAppProcessing } from '~/pages/ApplicationDetails/context';
import { DocumentCheckOutlineIcon, ArrowRightOutlineIcon } from '@applyboard/ui-icons';
import { Dialog, Button, Flex, Text, ButtonLink, useToasts } from '@applyboard/crystal-ui';
import { useIssueOfferActivity } from './useIssueOfferActivity';

import { AdditionalDetails } from './components/AdditionalDetails';
import { ConditionGroup } from './components/ConditionGroup';
import { DialogHeader } from './components/DialogHeader';
import { AdditionalDetailsType, STEPS, OfferType, OfferClassification } from './types';
import { ConditionType } from '~/pages/ApplicationDetails/data/useOfferConditions';
import { useSchoolCustomization } from '~/context/SchoolCustomizationsProvider';
import { useIntakeDetails } from '~/pages/IntakeDetails/data/useIntakeDetails';

const addSixHours = (dateStr: string | undefined | null): string | undefined => {
  if (!dateStr) return undefined;
  const date = new Date(dateStr);
  date.setUTCHours(date.getUTCHours() + 6);
  return date.toISOString();
};

export function OfferLetterDialog({
  applicationId,
  offerType,
  reissueOffer,
}: {
  applicationId: string;
  offerType: OfferType;
  reissueOffer?: boolean;
}) {
  const { apiError } = useErrorHandler();
  const { completeProcess, application, startProcess } = useAppProcessing();
  const { schoolCustomizations } = useSchoolCustomization();
  const programIntakeId = application.programIntake?.programIntakeId ?? '';
  const { intake } = useIntakeDetails(programIntakeId);
  const [loading, setLoading] = React.useState(false);
  const [downloadUrl, setDownloadUrl] = React.useState('');

  const [conditions, setConditions] = React.useState<Array<string>>(
    offerType === OfferType.PRE_OFFER
      ? application.applicationAdmissionConditions.filter((c) => c.code !== 'other').map((c) => c.code)
      : application.applicationFinalOfferConditions.filter((c) => c.code !== 'other').map((c) => c.code)
  );
  const [customCondition, setCustomCondition] = React.useState(
    offerType === OfferType.PRE_OFFER
      ? application.applicationAdmissionConditions.find((c) => c.code === 'other')?.name || ''
      : application.applicationFinalOfferConditions.find((c) => c.code === 'other')?.name || ''
  );
  const [conditionDeadline, setConditionDeadline] = React.useState<string | undefined>(undefined);

  // Determine initial offer classification based on persisted conditions
  // Default to Unconditional when no existing conditions (e.g., after reissuing as unconditional)
  const initialOfferClassification = React.useMemo(() => {
    const existingConditions =
      offerType === OfferType.PRE_OFFER
        ? application.applicationAdmissionConditions
        : application.applicationFinalOfferConditions;
    return existingConditions.length > 0 ? OfferClassification.CONDITIONAL : OfferClassification.UNCONDITIONAL;
  }, [offerType, application.applicationAdmissionConditions, application.applicationFinalOfferConditions]);

  const [offerClassification, setOfferClassification] = React.useState<OfferClassification | ''>(
    initialOfferClassification
  );
  const offerTermStartDate = application.offer?.termStartDate ?? undefined;
  const offerFirstDayOfClassOn = application.offer?.firstDayOfClassOn ?? undefined;
  const offerLastDayOfClassOn = application.offer?.lastDayOfClassOn ?? undefined;

  const [additionalDetails, setAdditionalDetails] = React.useState<AdditionalDetailsType>({
    firstPaymentFee: application.offer?.firstPaymentFee,
    firstPaymentDate: application.offer?.firstPaymentDate,
    secondPaymentFee: application.offer?.secondPaymentFee,
    secondPaymentDate: application.offer?.secondPaymentDate,
    tuitionFee:
      application.offer?.tuitionFee ?? application.programIntake?.tuition ?? application.programIntake.program.tuition,
    ancillaryFee: application.offer?.ancillaryFee,
    additionalNotes: application.offer?.additionalNotes,
    termStartDate: offerTermStartDate ?? application.programIntake?.intakeTerm?.startDate,
    firstDayOfClassOn: offerFirstDayOfClassOn,
    lastDayOfClassOn: offerLastDayOfClassOn,
  });

  // Update intake date fields when intake data is loaded (only if not already set from offer)
  React.useEffect(() => {
    if (intake) {
      setAdditionalDetails((prev) => ({
        ...prev,
        termStartDate: prev.termStartDate || addSixHours(application.programIntake?.intakeTerm?.startDate),
        firstDayOfClassOn: prev.firstDayOfClassOn || addSixHours(intake.firstDayOfClass) || '',
        lastDayOfClassOn: prev.lastDayOfClassOn || addSixHours(intake.lastDayOfClass) || '',
      }));
    }
  }, [
    intake,
    application.programIntake?.intakeTerm?.startDate,
    offerTermStartDate,
    offerFirstDayOfClassOn,
    offerLastDayOfClassOn,
  ]);

  const [stepState, setStepState] = React.useState<number>(STEPS.STEP1);
  const activity = useIssueOfferActivity(applicationId, offerType);
  const toasts = useToasts();

  // Handle offer classification change - clear conditions when switching to Unconditional
  const handleOfferClassificationChange = React.useCallback((classification: OfferClassification) => {
    setOfferClassification(classification);
    if (classification === OfferClassification.UNCONDITIONAL) {
      setConditions([]);
      setCustomCondition('');
      setConditionDeadline(undefined);
    }
  }, []);

  const onOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        setStepState(STEPS.STEP1);
        setCustomCondition('');
        setConditions([]);
        setOfferClassification(initialOfferClassification);
        completeProcess();
        setConditionDeadline('');
        setAdditionalDetails({
          firstPaymentFee: undefined,
          firstPaymentDate: undefined,
          secondPaymentFee: undefined,
          secondPaymentDate: undefined,
          tuitionFee: undefined,
          ancillaryFee: undefined,
          additionalNotes: undefined,
          termStartDate: undefined,
          firstDayOfClassOn: undefined,
          lastDayOfClassOn: undefined,
        });
      }
    },
    [
      setStepState,
      setCustomCondition,
      setConditions,
      setOfferClassification,
      initialOfferClassification,
      completeProcess,
      setConditionDeadline,
      setAdditionalDetails,
    ]
  );

  const offerTypeText = React.useMemo(
    () => (offerType === OfferType.PRE_OFFER ? 'pre-offer' : 'final offer'),
    [offerType]
  );

  const onSubmit = React.useCallback(() => {
    setLoading(true);
    activity
      .issueOffer(conditions, customCondition, offerType, additionalDetails)
      .then((hasInitialOfferActions) => {
        if (hasInitialOfferActions) {
          startProcess('additionalDocumentRequest');
        } else {
          completeProcess();
        }
        toasts.positive(`Successfully sent ${offerTypeText} letter. It will be available in details tab shortly.`);
      })
      .catch(() => apiError())
      .finally(() => setLoading(false));
  }, [
    setLoading,
    activity,
    conditions,
    customCondition,
    offerType,
    toasts,
    apiError,
    additionalDetails,
    startProcess,
    completeProcess,
    offerTypeText,
  ]);

  const onNextButtonClick = React.useCallback(
    (step: number) => {
      if (step === STEPS.STEP1) {
        setStepState(STEPS.STEP2);
        return;
      }

      if (step === STEPS.STEP2) {
        setLoading(true);
        activity
          .generateOffer({
            conditions,
            customCondition,
            deferralRequestId: undefined,
            additionalNotes: additionalDetails.additionalNotes ?? undefined,
            conditionDeadline: conditionDeadline ?? undefined,
            additionalDetails: additionalDetails,
          })
          .then(({ letterPreviewDownloadUrl }) => {
            setDownloadUrl(letterPreviewDownloadUrl);
            setStepState(STEPS.STEP3);
          })
          .catch(() => apiError())
          .finally(() => {
            setLoading(false);
          });
      }
    },
    [
      setStepState,
      setLoading,
      activity,
      conditions,
      customCondition,
      apiError,
      additionalDetails,
      conditionDeadline,
      setDownloadUrl,
    ]
  );

  const enableFeeFields = React.useMemo(
    () =>
      (offerType === OfferType.PRE_OFFER && schoolCustomizations?.schoolCustomization.enablePreOfferLetterFeeFields) ||
      (offerType === OfferType.FINAL_OFFER &&
        schoolCustomizations?.schoolCustomization.enableFinalOfferLetterFeeFields),
    [offerType, schoolCustomizations]
  );

  const enableConditionDeadline = React.useMemo(
    () =>
      (offerType === OfferType.PRE_OFFER &&
        schoolCustomizations?.schoolCustomization.enablePreOfferLetterConditionDeadlineField) ||
      (offerType === OfferType.FINAL_OFFER &&
        schoolCustomizations?.schoolCustomization.enableFinalOfferLetterConditionDeadlineField),
    [offerType, schoolCustomizations]
  );

  // Validation: Offer Type must be selected, and if Conditional, at least one condition must be selected
  const isStep2Valid = React.useMemo(() => {
    if (offerClassification === '') {
      return false;
    }
    if (offerClassification === OfferClassification.UNCONDITIONAL) {
      return true;
    }
    // Conditional: must have at least one predefined condition OR a custom condition
    return conditions.length > 0 || customCondition.trim() !== '';
  }, [offerClassification, conditions, customCondition]);

  const offerTitle = React.useMemo(
    () => (offerType === OfferType.PRE_OFFER ? 'Pre-offer LOA' : 'final-offer LOA'),
    [offerType]
  );

  const sendCommand = React.useMemo(
    () => (offerType === OfferType.PRE_OFFER ? 'Send Pre-offer' : 'Send Final Offer'),
    [offerType]
  );

  let thirdScreenTitleText = '';
  if (offerType === OfferType.PRE_OFFER) {
    thirdScreenTitleText = reissueOffer ? 'Reissue Initial Offer' : 'Issue Initial Offer';
  } else {
    thirdScreenTitleText = reissueOffer ? 'Reissue Final Offer' : 'Issue Final Offer';
  }

  const thirdScreenTitle = React.useMemo(() => thirdScreenTitleText, [thirdScreenTitleText]);

  return (
    <Dialog onOpenChange={onOpenChange} size="lg" open={true}>
      <Dialog.Heading>
        <Flex direction="row" gap={2}>
          {stepState === STEPS.STEP1 && (
            <DialogHeader
              setStepState={setStepState}
              stepState={stepState}
              heading="Provide Additional Details"
              totalSteps={3}
            />
          )}
          {stepState === STEPS.STEP2 && (
            <DialogHeader
              setStepState={setStepState}
              stepState={stepState}
              heading="Select Conditions"
              totalSteps={3}
            />
          )}
          {stepState === STEPS.STEP3 && (
            <DialogHeader setStepState={setStepState} stepState={stepState} heading={thirdScreenTitle} totalSteps={3} />
          )}
        </Flex>
      </Dialog.Heading>
      <Dialog.Content>
        <Flex direction="column" gap={8}>
          {stepState === STEPS.STEP1 && (
            <AdditionalDetails
              setAdditionalDetails={setAdditionalDetails}
              additionalDetails={additionalDetails}
              enableFeeFields={enableFeeFields ?? false}
            />
          )}
          {stepState === STEPS.STEP2 && (
            <ConditionGroup
              conditions={conditions}
              setConditions={setConditions}
              customCondition={customCondition}
              setCustomCondition={setCustomCondition}
              conditionDeadline={conditionDeadline}
              setConditionDeadline={setConditionDeadline}
              enableConditionDeadline={enableConditionDeadline ?? false}
              conditionType={
                offerType === OfferType.PRE_OFFER
                  ? ConditionType.ADMISSION_CONDITIONS
                  : ConditionType.FINAL_OFFER_CONDITIONS
              }
              offerClassification={offerClassification}
              setOfferClassification={handleOfferClassificationChange}
            />
          )}
          {stepState === STEPS.STEP3 && (
            <>
              <ButtonLink
                aria-label="Download offer LOA"
                emphasis="highlighted"
                href={downloadUrl}
                intent="primary"
                size="lg"
                leadIcon={DocumentCheckOutlineIcon}
                width="fill"
                mode="override_user_choice_and_open_in_new_tab"
              >
                {offerTitle}
              </ButtonLink>
              <Text variant="bodyM" intent="secondary">
                {`A preview of the ${offerTypeText} letter is available before sending it to the applicant. Once the letter is
                sent, you will not be able to make changes.`}
              </Text>
            </>
          )}
        </Flex>
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton disabled={loading} width="fill">
          Cancel
        </Dialog.CloseButton>
        {(stepState === STEPS.STEP1 || stepState === STEPS.STEP2) && (
          <Button
            intent="primary"
            loading={loading}
            disabled={stepState === STEPS.STEP2 && !isStep2Valid}
            width="fill"
            trailIcon={ArrowRightOutlineIcon}
            onClick={() => onNextButtonClick(stepState)}
          >
            Next
          </Button>
        )}
        {stepState === STEPS.STEP3 && (
          <Button intent="primary" loading={loading} onClick={onSubmit} width="fill">
            {sendCommand}
          </Button>
        )}
      </Dialog.Actions>
    </Dialog>
  );
}
